import React, { memo } from "react";
import { getName } from "country-list";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import ButtonComponent from "../../../components/ButtonMC";
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import useCustomerAddressesData from "../../../hooks/inbox/data/useCustomerAddressesData";
import useCustomerAddressesUI from "../../../hooks/inbox/ui/useCustomerAddressesUI";
import IconMC from "../../../components/IconMC";
import { getFlagIcon } from "../../../../services/countryService";
import LoadingMC from "../../../components/LoadingMC";

/**
 * Componente Organismo para gestión de direcciones de cliente en el Inbox.
 *
 * - Separa lógica de negocio (data/ui) en hooks especializados.
 * - Optimizado con React.memo, useCallback y useMemo según estándares Inbox.
 * - TailwindCSS aplicado según equivalentes de estilos originales.
 *
 * @component CustomerAddressesMC
 * @category Component - Organism
 * @since 3.1.0
 * @version 3.2.0 - Refactor a arquitectura optimizada y Tailwind
 */

const CustomerAddressesMC = memo(({ customer }) => {
  const { t } = useTranslation('InboxMC');
  // Data Layer: carga y gestión de direcciones
  const {
    addresses,
    loading,
    loadAddresses,
    removeAddress,
  } = useCustomerAddressesData(customer);

  // UI Layer: estado visual y handlers
  const {
    openCreateAddress,
    toggleEditAddress
  } = useCustomerAddressesUI({ customer, loadAddresses });

  // 🚀 DEBUG RENDER - Rastrear re-renders en CustomerAddressesMC (OPTIMIZADO)
  useTraceUpdate('CustomerAddressesMC', {
    customerId: customer?.id,
    web_id: customer?.web_id,
    addressesLength: addresses?.length || 0,
    loading
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper tw-flex tw-flex-col tw-gap-4 tw-mt-4">
      <div className="tw-flex tw-justify-center">
        <ButtonComponent
          outlined
          text={t('location.create_address')}
          onClick={() => openCreateAddress()}
          className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
        />
      </div>
      <LoadingMC loading={loading} />
      {addresses.map((address) => (
        <div
          key={`address-${address.id}`}
          className="tw-flex tw-flex-col tw-rounded-2xl tw-p-4 tw-text-gray-500 tw-text-xs tw-border tw-border-gray-200"
        >
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-xs tw-text-gray-950 tw-font-medium">
              {t('location.address')}
            </span>
            <div className="tw-flex tw-flex-row tw-justify-end tw-items-center tw-gap-1 tw-mb-2">
              {address.main && (
                <div className="actions-address-icon-tooltip" data-tooltip-content={t('location.main_address')}>
                  <IconMC name="checkmark-circle-2-fill" size="base" className="tw-text-blue-500" />
                </div>
              )}
              <div className="actions-address-icon-tooltip" data-tooltip-content={t('location.edit_tooltip')}>
                <IconMC name="edit-outline" size="base" onClick={() => toggleEditAddress(address)} />
              </div>
              <div className="actions-address-icon-tooltip" data-tooltip-content={t('location.delete_tooltip')}>
                <IconMC name="trash-2-outline" size="base" onClick={() => removeAddress(address)} />
              </div>
              <ReactTooltip
                place="top"
                anchorSelect=".actions-address-icon-tooltip"
                className="tw-z-50"
              />
            </div>
          </div>
          <span className="tw-break-all">{`${t('location.address')}:  ${address.address}`}</span>
          <span className="tw-mt-1 tw-break-all">{`${t('location.description')}:  ${address.description}`}</span>
          <span className="tw-mt-1">{`${t('location.city')}:  ${address.city}`}</span>
          <span className="tw-mt-1">{`${t('location.state')}:  ${address.state}`}</span>
          <span className="tw-mt-1">{`${t('location.zip_code')}:  ${address.zip_code}`}</span>
          <span className="tw-mt-1">
            {t('location.country')}
            {': '}
            {getFlagIcon(address.country_id, 'tw-mr-1')}
            {getName(address.country_id)}
          </span>
        </div>
      ))}
    </div>
  );
});

CustomerAddressesMC.displayName = "CustomerAddressesMC";

export default CustomerAddressesMC;
